import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Trash2, Phone, Radio, X } from 'lucide-react';
import { getToken } from '../lib/api';
import { useToast } from '../components/Toast';

const auth = () => ({ headers: { Authorization: `Bearer ${getToken()}` } });
const fJson = async (url: string, init: RequestInit = {}) => {
  const r = await fetch(url, { ...init, headers: { 'Content-Type': 'application/json', ...auth().headers, ...(init.headers || {}) } });
  if (!r.ok) throw new Error(await r.text());
  return r.json();
};

const VENDORS = ['Grandstream', 'Snom', 'Yealink', 'Cisco', 'Other'];

type Line = { extension_uuid: string; label: string };
type Device = { id?: string; name: string; type: 'dect' | 'fxs'; vendor: string; model: string; mac: string; ip: string; lines: Line[] };

const empty = (): Device => ({ name: '', type: 'dect', vendor: 'Grandstream', model: '', mac: '', ip: '', lines: [] });

export default function DectFxs() {
  const qc = useQueryClient();
  const toast = useToast();
  const devices = useQuery<Device[]>({ queryKey: ['dect'], queryFn: () => fJson('/api/dect') });
  const exts = useQuery<any[]>({ queryKey: ['users'], queryFn: () => fJson('/api/users') });
  const [editing, setEditing] = useState<Device | null>(null);

  const save = useMutation({
    mutationFn: (d: Device) => d.id
      ? fJson(`/api/dect/${d.id}`, { method: 'PUT', body: JSON.stringify(d) })
      : fJson('/api/dect', { method: 'POST', body: JSON.stringify(d) }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['dect'] }); setEditing(null); toast('success', 'Saved'); },
    onError: (e: any) => toast('error', e.message),
  });
  const remove = useMutation({
    mutationFn: (id: string) => fJson(`/api/dect/${id}`, { method: 'DELETE' }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['dect'] }); toast('success', 'Deleted'); },
    onError: (e: any) => toast('error', e.message),
  });

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold">DECT / FXS Devices</h1>
        <button onClick={() => setEditing(empty())} className="btn-primary inline-flex items-center gap-2"><Plus size={14} /> Add Device</button>
      </div>
      <p className="text-sm text-textSecondary">Cordless DECT base stations and analog FXS gateways (ATAs). Bind each handset / analog port to an extension.</p>

      <div className="card p-0 overflow-hidden">
        <table className="table">
          <thead><tr><th></th><th>Name</th><th>Type</th><th>Vendor / Model</th><th>MAC</th><th>IP</th><th>Lines</th><th></th></tr></thead>
          <tbody>
            {(devices.data || []).map(d => (
              <tr key={d.id} className="hover:bg-elevated cursor-pointer" onClick={() => setEditing({ ...empty(), ...d, lines: d.lines || [] })}>
                <td>{d.type === 'fxs' ? <Phone size={14} className="text-textSecondary" /> : <Radio size={14} className="text-textSecondary" />}</td>
                <td className="font-medium">{d.name}</td>
                <td className="uppercase text-xs">{d.type}</td>
                <td className="text-sm">{d.vendor} {d.model}</td>
                <td className="font-mono text-xs">{d.mac || '—'}</td>
                <td className="font-mono text-xs">{d.ip || '—'}</td>
                <td className="text-xs">{(d.lines || []).length}</td>
                <td onClick={e => e.stopPropagation()}>
                  <button className="btn-ghost p-1 text-danger" onClick={() => d.id && confirm('Delete this device?') && remove.mutate(d.id)}><Trash2 size={14} /></button>
                </td>
              </tr>
            ))}
            {!devices.data?.length && <tr><td colSpan={8} className="text-center py-8 text-textSecondary">No DECT/FXS devices yet.</td></tr>}
          </tbody>
        </table>
      </div>

      {editing && <DeviceForm device={editing} exts={exts.data || []} saving={save.isPending} onClose={() => setEditing(null)} onSave={(d: Device) => save.mutate(d)} />}
    </div>
  );
}

function DeviceForm({ device, exts, saving, onClose, onSave }: any) {
  const [d, setD] = useState<Device>(device);
  const set = (k: keyof Device, v: any) => setD({ ...d, [k]: v });
  const setLine = (i: number, k: keyof Line, v: any) => { const lines = [...d.lines]; lines[i] = { ...lines[i], [k]: v }; setD({ ...d, lines }); };
  const addLine = () => setD({ ...d, lines: [...d.lines, { extension_uuid: '', label: '' }] });
  const delLine = (i: number) => setD({ ...d, lines: d.lines.filter((_, j) => j !== i) });

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="card max-w-2xl w-full max-h-[90vh] overflow-auto p-5 space-y-4" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <h2 className="text-xl font-semibold">{d.id ? 'Edit' : 'Add'} DECT / FXS Device</h2>
          <button className="btn-ghost p-1" onClick={onClose}><X size={16} /></button>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <label className="text-sm">Name<input className="input mt-1" value={d.name} onChange={e => set('name', e.target.value)} placeholder="Front desk DECT" /></label>
          <label className="text-sm">Type
            <select className="input mt-1" value={d.type} onChange={e => set('type', e.target.value as any)}>
              <option value="dect">DECT (cordless)</option>
              <option value="fxs">FXS (analog ATA)</option>
            </select>
          </label>
          <label className="text-sm">Vendor
            <select className="input mt-1" value={d.vendor} onChange={e => set('vendor', e.target.value)}>{VENDORS.map(v => <option key={v}>{v}</option>)}</select>
          </label>
          <label className="text-sm">Model<input className="input mt-1" value={d.model} onChange={e => set('model', e.target.value)} placeholder="DP750 / HT812" /></label>
          <label className="text-sm">MAC<input className="input mt-1 font-mono" value={d.mac} onChange={e => set('mac', e.target.value)} placeholder="00:0b:82:xx:xx:xx" /></label>
          <label className="text-sm">IP<input className="input mt-1 font-mono" value={d.ip} onChange={e => set('ip', e.target.value)} placeholder="192.168.x.x" /></label>
        </div>

        <div>
          <div className="flex items-center justify-between mb-1">
            <div className="text-sm font-semibold">{d.type === 'fxs' ? 'Analog ports' : 'Handsets'} → extensions</div>
            <button className="btn-ghost text-sm inline-flex items-center gap-1" onClick={addLine}><Plus size={12} /> Add line</button>
          </div>
          <div className="space-y-2">
            {d.lines.map((ln, i) => (
              <div key={i} className="flex gap-2 items-center">
                <input className="input w-40" value={ln.label} onChange={e => setLine(i, 'label', e.target.value)} placeholder={`${d.type === 'fxs' ? 'Port' : 'Handset'} ${i + 1}`} />
                <select className="input flex-1" value={ln.extension_uuid} onChange={e => setLine(i, 'extension_uuid', e.target.value)}>
                  <option value="">— Extension —</option>
                  {exts.map((u: any) => <option key={u.extension_uuid} value={u.extension_uuid}>{u.extension} — {u.name || ''}</option>)}
                </select>
                <button className="btn-ghost p-1 text-danger" onClick={() => delLine(i)}><Trash2 size={14} /></button>
              </div>
            ))}
            {!d.lines.length && <div className="text-xs text-textSecondary">No lines yet — add one to bind a handset/port to an extension.</div>}
          </div>
        </div>

        <div className="flex justify-end gap-2 pt-2 border-t border-border">
          <button className="btn-ghost" onClick={onClose}>Cancel</button>
          <button className="btn-primary" disabled={saving || !d.name} onClick={() => onSave(d)}>{saving ? 'Saving…' : 'Save'}</button>
        </div>
      </div>
    </div>
  );
}
